import { Type } from "@sinclair/typebox";
import { NonEmptyString } from "./primitives.js";

export const ModelChoiceSchema = Type.Object(
  {
    id: NonEmptyString,
    name: NonEmptyString,
    provider: NonEmptyString,
    contextWindow: Type.Optional(Type.Integer({ minimum: 1 })),
    reasoning: Type.Optional(Type.Boolean()),
  },
  { additionalProperties: false },
);

export const AgentSummarySchema = Type.Object(
  {
    id: NonEmptyString,
    name: Type.Optional(NonEmptyString),
    identity: Type.Optional(
      Type.Object(
        {
          name: Type.Optional(NonEmptyString),
          theme: Type.Optional(NonEmptyString),
          emoji: Type.Optional(NonEmptyString),
          avatar: Type.Optional(NonEmptyString),
          avatarUrl: Type.Optional(NonEmptyString),
        },
        { additionalProperties: false },
      ),
    ),
  },
  { additionalProperties: false },
);

export const AgentsListParamsSchema = Type.Object({}, { additionalProperties: false });

export const AgentsListResultSchema = Type.Object(
  {
    defaultId: NonEmptyString,
    mainKey: NonEmptyString,
    scope: Type.Union([Type.Literal("per-sender"), Type.Literal("global")]),
    agents: Type.Array(AgentSummarySchema),
  },
  { additionalProperties: false },
);

export const AgentsFileEntrySchema = Type.Object(
  {
    name: NonEmptyString,
    path: NonEmptyString,
    missing: Type.Boolean(),
    size: Type.Optional(Type.Integer({ minimum: 0 })),
    updatedAtMs: Type.Optional(Type.Integer({ minimum: 0 })),
    content: Type.Optional(Type.String()),
  },
  { additionalProperties: false },
);

export const AgentsFilesListParamsSchema = Type.Object(
  {
    agentId: NonEmptyString,
  },
  { additionalProperties: false },
);

export const AgentsFilesListResultSchema = Type.Object(
  {
    agentId: NonEmptyString,
    workspace: NonEmptyString,
    files: Type.Array(AgentsFileEntrySchema),
  },
  { additionalProperties: false },
);

export const AgentsFilesGetParamsSchema = Type.Object(
  {
    agentId: NonEmptyString,
    name: NonEmptyString,
  },
  { additionalProperties: false },
);

export const AgentsFilesGetResultSchema = Type.Object(
  {
    agentId: NonEmptyString,
    workspace: NonEmptyString,
    file: AgentsFileEntrySchema,
  },
  { additionalProperties: false },
);

export const AgentsFilesSetParamsSchema = Type.Object(
  {
    agentId: NonEmptyString,
    name: NonEmptyString,
    content: Type.String(),
  },
  { additionalProperties: false },
);

export const AgentsFilesSetResultSchema = Type.Object(
  {
    ok: Type.Literal(true),
    agentId: NonEmptyString,
    workspace: NonEmptyString,
    file: AgentsFileEntrySchema,
  },
  { additionalProperties: false },
);

export const WorkspaceTreeNodeSchema = Type.Recursive((Self) =>
  Type.Object(
    {
      name: NonEmptyString,
      path: NonEmptyString,
      kind: Type.Union([Type.Literal("file"), Type.Literal("dir")]),
      size: Type.Optional(Type.Integer({ minimum: 0 })),
      updatedAtMs: Type.Optional(Type.Integer({ minimum: 0 })),
      children: Type.Optional(Type.Array(Self)),
    },
    { additionalProperties: false },
  ),
);

export const AgentsWorkspaceListParamsSchema = Type.Object(
  {
    agentId: NonEmptyString,
    sessionKey: NonEmptyString,
    projectId: Type.Optional(NonEmptyString),
  },
  { additionalProperties: false },
);

export const AgentsWorkspaceListResultSchema = Type.Object(
  {
    agentId: NonEmptyString,
    sessionKey: NonEmptyString,
    workspace: NonEmptyString,
    isolated: Type.Boolean(),
    scope: Type.Union([Type.Literal("sandbox"), Type.Literal("session")]),
    truncated: Type.Boolean(),
    tree: Type.Array(WorkspaceTreeNodeSchema),
    projectId: Type.Optional(NonEmptyString),
  },
  { additionalProperties: false },
);

export const AgentsWorkspaceReadParamsSchema = Type.Object(
  {
    agentId: NonEmptyString,
    sessionKey: NonEmptyString,
    path: NonEmptyString,
    projectId: Type.Optional(NonEmptyString),
  },
  { additionalProperties: false },
);

export const FleetProjectSummarySchema = Type.Object(
  {
    id: NonEmptyString,
    slug: NonEmptyString,
    name: NonEmptyString,
    agentId: NonEmptyString,
    sessionKey: NonEmptyString,
    originSessionKey: Type.Optional(NonEmptyString),
    rootPath: NonEmptyString,
    createdAtMs: Type.Integer({ minimum: 0 }),
    updatedAtMs: Type.Integer({ minimum: 0 }),
    fileCount: Type.Integer({ minimum: 0 }),
  },
  { additionalProperties: false },
);

export const FleetProjectsListParamsSchema = Type.Object(
  {
    agentId: NonEmptyString,
    originSessionKey: Type.Optional(NonEmptyString),
    /**
     * Cap on returned projects. Server clamps to [1, MAX_FLEET_PROJECTS_LIMIT].
     * Total/all counts stay accurate — only `projects` is sliced for render.
     */
    limit: Type.Optional(Type.Integer({ minimum: 1, maximum: 200 })),
  },
  { additionalProperties: false },
);

export const FleetProjectsListResultSchema = Type.Object(
  {
    agentId: NonEmptyString,
    projects: Type.Array(FleetProjectSummarySchema),
    /** True when the response `projects` slice hit the `limit` cap. */
    truncated: Type.Optional(Type.Boolean()),
    /** Effective limit applied (after clamping). */
    appliedLimit: Type.Optional(Type.Integer({ minimum: 1 })),
    /** Total projects found in the registry before slicing. */
    total: Type.Optional(Type.Integer({ minimum: 0 })),
  },
  { additionalProperties: false },
);

export const FleetProjectsCreateParamsSchema = Type.Object(
  {
    agentId: NonEmptyString,
    name: NonEmptyString,
    parentSessionKey: Type.Optional(NonEmptyString),
  },
  { additionalProperties: false },
);

export const FleetProjectsCreateResultSchema = Type.Object(
  {
    project: FleetProjectSummarySchema,
    sessionKey: NonEmptyString,
    sessionId: NonEmptyString,
    label: NonEmptyString,
  },
  { additionalProperties: false },
);

export const FleetProjectsGetParamsSchema = Type.Object(
  {
    agentId: NonEmptyString,
    projectId: NonEmptyString,
  },
  { additionalProperties: false },
);

export const FleetProjectsGetResultSchema = Type.Object(
  {
    project: FleetProjectSummarySchema,
  },
  { additionalProperties: false },
);

export const FleetProjectsUpdateParamsSchema = Type.Object(
  {
    agentId: NonEmptyString,
    projectId: NonEmptyString,
    name: NonEmptyString,
  },
  { additionalProperties: false },
);

export const FleetProjectsUpdateResultSchema = Type.Object(
  {
    project: FleetProjectSummarySchema,
  },
  { additionalProperties: false },
);

export const FleetProjectsDeleteParamsSchema = Type.Object(
  {
    agentId: NonEmptyString,
    projectId: NonEmptyString,
    confirm: Type.Literal(true),
  },
  { additionalProperties: false },
);

export const FleetProjectsDeleteResultSchema = Type.Object(
  {
    ok: Type.Literal(true),
    projectId: NonEmptyString,
    sessionKey: NonEmptyString,
  },
  { additionalProperties: false },
);

export const AgentsWorkspaceReadResultSchema = Type.Object(
  {
    agentId: NonEmptyString,
    sessionKey: NonEmptyString,
    workspace: NonEmptyString,
    isolated: Type.Boolean(),
    scope: Type.Union([Type.Literal("sandbox"), Type.Literal("session")]),
    path: NonEmptyString,
    size: Type.Integer({ minimum: 0 }),
    updatedAtMs: Type.Integer({ minimum: 0 }),
    content: Type.String(),
    truncated: Type.Boolean(),
    projectId: Type.Optional(NonEmptyString),
  },
  { additionalProperties: false },
);

export const AgentsFleetGraphParamsSchema = Type.Object(
  {
    sessionKey: Type.Optional(Type.String()),
  },
  { additionalProperties: false },
);

export const AgentsFleetGraphNodeSchema = Type.Object(
  {
    id: NonEmptyString,
    label: NonEmptyString,
    kind: Type.Union([Type.Literal("agent"), Type.Literal("inbound")]),
  },
  { additionalProperties: false },
);

export const AgentsFleetGraphEdgeSchema = Type.Object(
  {
    from: NonEmptyString,
    to: NonEmptyString,
    kind: Type.Union([Type.Literal("spawn"), Type.Literal("route"), Type.Literal("escalation")]),
    label: Type.Optional(Type.String()),
    bindingId: Type.Optional(Type.String()),
  },
  { additionalProperties: false },
);

export const AgentsFleetGraphResultSchema = Type.Object(
  {
    nodes: Type.Array(AgentsFleetGraphNodeSchema),
    edges: Type.Array(AgentsFleetGraphEdgeSchema),
    sessionAgentId: Type.Optional(NonEmptyString),
  },
  { additionalProperties: false },
);

export const ModelsListParamsSchema = Type.Object({}, { additionalProperties: false });

export const ModelsListResultSchema = Type.Object(
  {
    models: Type.Array(ModelChoiceSchema),
  },
  { additionalProperties: false },
);

export const SkillsStatusParamsSchema = Type.Object(
  {
    agentId: Type.Optional(NonEmptyString),
  },
  { additionalProperties: false },
);

export const SkillsBinsParamsSchema = Type.Object({}, { additionalProperties: false });

export const SkillsBinsResultSchema = Type.Object(
  {
    bins: Type.Array(NonEmptyString),
  },
  { additionalProperties: false },
);

export const SkillsInstallParamsSchema = Type.Object(
  {
    name: NonEmptyString,
    installId: NonEmptyString,
    timeoutMs: Type.Optional(Type.Integer({ minimum: 1000 })),
  },
  { additionalProperties: false },
);

export const SkillsUpdateParamsSchema = Type.Object(
  {
    skillKey: NonEmptyString,
    enabled: Type.Optional(Type.Boolean()),
    apiKey: Type.Optional(Type.String()),
    env: Type.Optional(Type.Record(NonEmptyString, Type.String())),
  },
  { additionalProperties: false },
);

export const SessionArtifactRecordSchema = Type.Object(
  {
    id: NonEmptyString,
    ts: Type.Integer({ minimum: 0 }),
    sessionKey: NonEmptyString,
    agentId: NonEmptyString,
    tool: NonEmptyString,
    path: NonEmptyString,
    op: Type.Union([
      Type.Literal("created"),
      Type.Literal("modified"),
      Type.Literal("deleted"),
      Type.Literal("manual"),
    ]),
    shaBefore: Type.Optional(NonEmptyString),
    shaAfter: Type.Optional(NonEmptyString),
    beforeBytes: Type.Optional(Type.Integer({ minimum: 0 })),
    afterBytes: Type.Optional(Type.Integer({ minimum: 0 })),
    projectId: Type.Optional(NonEmptyString),
  },
  { additionalProperties: false },
);

export const SessionsArtifactsListParamsSchema = Type.Object(
  {
    agentId: NonEmptyString,
    sessionKey: NonEmptyString,
    projectId: Type.Optional(NonEmptyString),
    limit: Type.Optional(Type.Integer({ minimum: 1, maximum: 200 })),
  },
  { additionalProperties: false },
);

export const SessionsArtifactsListResultSchema = Type.Object(
  {
    workspace: NonEmptyString,
    changes: Type.Array(SessionArtifactRecordSchema),
    truncated: Type.Boolean(),
  },
  { additionalProperties: false },
);

export const SessionsArtifactsGetParamsSchema = Type.Object(
  {
    agentId: NonEmptyString,
    sessionKey: NonEmptyString,
    changeId: NonEmptyString,
    projectId: Type.Optional(NonEmptyString),
  },
  { additionalProperties: false },
);

export const SessionsArtifactsGetResultSchema = Type.Object(
  {
    change: SessionArtifactRecordSchema,
    before: Type.Union([Type.String(), Type.Null()]),
    after: Type.Union([Type.String(), Type.Null()]),
    diff: Type.String(),
    beforeTruncated: Type.Boolean(),
    afterTruncated: Type.Boolean(),
  },
  { additionalProperties: false },
);

export const SessionArtifactFileSummarySchema = Type.Object(
  {
    path: NonEmptyString,
    op: Type.Union([
      Type.Literal("created"),
      Type.Literal("modified"),
      Type.Literal("deleted"),
      Type.Literal("manual"),
    ]),
    additions: Type.Integer({ minimum: 0 }),
    deletions: Type.Integer({ minimum: 0 }),
    sessionKey: Type.Optional(NonEmptyString),
  },
  { additionalProperties: false },
);

export const SessionArtifactSummaryTotalsSchema = Type.Object(
  {
    files: Type.Integer({ minimum: 0 }),
    created: Type.Integer({ minimum: 0 }),
    modified: Type.Integer({ minimum: 0 }),
    deleted: Type.Integer({ minimum: 0 }),
    additions: Type.Integer({ minimum: 0 }),
    deletions: Type.Integer({ minimum: 0 }),
  },
  { additionalProperties: false },
);

export const SessionsArtifactsSummaryParamsSchema = Type.Object(
  {
    agentId: NonEmptyString,
    sessionKey: Type.Optional(NonEmptyString),
    sessionKeys: Type.Optional(Type.Array(NonEmptyString, { minItems: 1, maxItems: 50 })),
    projectId: Type.Optional(NonEmptyString),
    sinceTs: Type.Optional(Type.Integer({ minimum: 0 })),
    /**
     * Cap on `files` returned in the render view. Totals are always computed
     * from the full deduped set, so client caps never distort them.
     * Default 30 — matches the inspector above-the-fold guidance.
     * Range [1, 200]; clamped server-side.
     */
    limit: Type.Optional(Type.Integer({ minimum: 1, maximum: 200 })),
  },
  { additionalProperties: false },
);

export const SessionsArtifactsSummaryResultSchema = Type.Object(
  {
    workspace: Type.String(),
    files: Type.Array(SessionArtifactFileSummarySchema),
    totals: SessionArtifactSummaryTotalsSchema,
    /** True when the underlying artifact registry was truncated at source. */
    truncated: Type.Optional(Type.Boolean()),
    /** True when the response `files` slice hit the `limit` cap. Totals still reflect all files. */
    renderTruncated: Type.Optional(Type.Boolean()),
    /** Effective limit applied (after clamping) when `files` was sliced. */
    appliedLimit: Type.Optional(Type.Integer({ minimum: 1 })),
    bySession: Type.Optional(
      Type.Record(NonEmptyString, Type.Array(SessionArtifactFileSummarySchema)),
    ),
  },
  { additionalProperties: false },
);

export const SessionsArtifactsRevertParamsSchema = Type.Object(
  {
    agentId: NonEmptyString,
    sessionKey: NonEmptyString,
    changeId: Type.Optional(NonEmptyString),
    sinceTs: Type.Optional(Type.Integer({ minimum: 0 })),
    projectId: Type.Optional(NonEmptyString),
  },
  { additionalProperties: false },
);

export const SessionsArtifactsCheckpointsParamsSchema = Type.Object(
  {
    agentId: NonEmptyString,
    sessionKey: NonEmptyString,
    projectId: Type.Optional(NonEmptyString),
    limit: Type.Optional(Type.Integer({ minimum: 1, maximum: 100 })),
  },
  { additionalProperties: false },
);

export const AgentsWorkspaceWriteParamsSchema = Type.Object(
  {
    agentId: NonEmptyString,
    sessionKey: NonEmptyString,
    path: NonEmptyString,
    content: Type.String(),
    projectId: Type.Optional(NonEmptyString),
  },
  { additionalProperties: false },
);

export const AgentsWorkspaceWriteResultSchema = Type.Object(
  {
    ok: Type.Literal(true),
    change: SessionArtifactRecordSchema,
  },
  { additionalProperties: false },
);

export const FleetWorkUnitSchema = Type.Object(
  {
    id: NonEmptyString,
    title: NonEmptyString,
    description: Type.String(),
    acceptanceCriteria: Type.Array(Type.String()),
    dependsOn: Type.Array(NonEmptyString),
    targetAgentId: Type.Optional(NonEmptyString),
    requiredSkills: Type.Array(Type.String()),
    estimatedInputTokens: Type.Optional(Type.Integer({ minimum: 0 })),
    contextValid: Type.Boolean(),
    validationNotes: Type.Array(Type.String()),
    youtrackIssueId: Type.Optional(NonEmptyString),
    youtrackIssueReadable: Type.Optional(NonEmptyString),
    childSessionKey: Type.Optional(NonEmptyString),
    runId: Type.Optional(NonEmptyString),
    status: Type.Union([
      Type.Literal("planned"),
      Type.Literal("youtrack"),
      Type.Literal("running"),
      Type.Literal("in_review"),
      Type.Literal("paused"),
      Type.Literal("blocked"),
      Type.Literal("stopped"),
      Type.Literal("done"),
      Type.Literal("failed"),
      Type.Literal("cancelled"),
    ]),
    revision: Type.Optional(Type.Integer({ minimum: 0 })),
    blockedReason: Type.Optional(Type.String()),
    priority: Type.Integer({ minimum: 1 }),
  },
  { additionalProperties: false },
);

export const FleetWorkPlanRecordSchema = Type.Object(
  {
    id: NonEmptyString,
    agentId: NonEmptyString,
    plannerAgentId: NonEmptyString,
    goal: NonEmptyString,
    youtrackProject: Type.Optional(NonEmptyString),
    parentSessionKey: Type.Optional(NonEmptyString),
    parentYoutrackIssueId: Type.Optional(NonEmptyString),
    parentYoutrackIssueReadable: Type.Optional(NonEmptyString),
    status: Type.Union([
      Type.Literal("draft"),
      Type.Literal("approved"),
      Type.Literal("published"),
      Type.Literal("stopping"),
      Type.Literal("stopped"),
      Type.Literal("completed"),
      Type.Literal("failed"),
      Type.Literal("abandoned"),
    ]),
    units: Type.Array(FleetWorkUnitSchema),
    createdAtMs: Type.Integer({ minimum: 0 }),
    updatedAtMs: Type.Integer({ minimum: 0 }),
    maxRepairCycles: Type.Optional(Type.Integer({ minimum: 0 })),
    repairCycleCount: Type.Optional(Type.Integer({ minimum: 0 })),
    requiresDecision: Type.Optional(Type.Boolean()),
    decisionRequest: Type.Optional(Type.String()),
    stoppedAtMs: Type.Optional(Type.Integer({ minimum: 0 })),
    lastCheckpointAtMs: Type.Optional(Type.Integer({ minimum: 0 })),
    autonomyLevel: Type.Optional(
      Type.Union([
        Type.Literal("manual"),
        Type.Literal("confirmation"),
        Type.Literal("autonomous"),
      ]),
    ),
  },
  { additionalProperties: false },
);

export const FleetWorkPlanSummarySchema = Type.Object(
  {
    id: NonEmptyString,
    agentId: NonEmptyString,
    plannerAgentId: NonEmptyString,
    goal: NonEmptyString,
    youtrackProject: Type.Optional(NonEmptyString),
    parentSessionKey: Type.Optional(NonEmptyString),
    parentYoutrackIssueId: Type.Optional(NonEmptyString),
    parentYoutrackIssueReadable: Type.Optional(NonEmptyString),
    status: Type.Union([
      Type.Literal("draft"),
      Type.Literal("approved"),
      Type.Literal("published"),
      Type.Literal("completed"),
    ]),
    units: Type.Array(FleetWorkUnitSchema),
    createdAtMs: Type.Integer({ minimum: 0 }),
    updatedAtMs: Type.Integer({ minimum: 0 }),
    unitCount: Type.Integer({ minimum: 0 }),
    doneCount: Type.Integer({ minimum: 0 }),
  },
  { additionalProperties: false },
);

export const FleetWorkPlanListParamsSchema = Type.Object(
  { agentId: NonEmptyString },
  { additionalProperties: false },
);

export const FleetWorkPlanListResultSchema = Type.Object(
  {
    agentId: NonEmptyString,
    plans: Type.Array(FleetWorkPlanSummarySchema),
    youtrackConfigured: Type.Boolean(),
  },
  { additionalProperties: false },
);

export const FleetWorkPlanGetParamsSchema = Type.Object(
  {
    agentId: NonEmptyString,
    planId: NonEmptyString,
  },
  { additionalProperties: false },
);

export const FleetWorkPlanGetResultSchema = Type.Object(
  { plan: FleetWorkPlanRecordSchema },
  { additionalProperties: false },
);

export const FleetWorkPlanCreateParamsSchema = Type.Object(
  {
    agentId: NonEmptyString,
    plannerAgentId: NonEmptyString,
    goal: NonEmptyString,
    youtrackProject: Type.Optional(NonEmptyString),
    parentSessionKey: Type.Optional(NonEmptyString),
  },
  { additionalProperties: false },
);

export const FleetWorkPlanCreateResultSchema = Type.Object(
  { plan: FleetWorkPlanRecordSchema },
  { additionalProperties: false },
);

export const FleetWorkPlanApproveParamsSchema = Type.Object(
  {
    agentId: NonEmptyString,
    planId: NonEmptyString,
  },
  { additionalProperties: false },
);

export const FleetWorkPlanApproveResultSchema = Type.Object(
  { plan: FleetWorkPlanRecordSchema },
  { additionalProperties: false },
);

export const FleetWorkPlanPublishYouTrackParamsSchema = Type.Object(
  {
    agentId: NonEmptyString,
    planId: NonEmptyString,
    project: NonEmptyString,
    linkType: Type.Optional(NonEmptyString),
  },
  { additionalProperties: false },
);

export const FleetWorkPlanPublishYouTrackResultSchema = Type.Object(
  { plan: FleetWorkPlanRecordSchema },
  { additionalProperties: false },
);

export const FleetWorkPlanSpawnUnitParamsSchema = Type.Object(
  {
    agentId: NonEmptyString,
    planId: NonEmptyString,
    unitId: NonEmptyString,
    sessionKey: NonEmptyString,
    workerAgentId: Type.Optional(NonEmptyString),
  },
  { additionalProperties: false },
);

export const FleetWorkPlanSpawnUnitResultSchema = Type.Object(
  {
    plan: FleetWorkPlanRecordSchema,
    unitId: NonEmptyString,
    childSessionKey: NonEmptyString,
    runId: NonEmptyString,
    warning: Type.Optional(Type.String()),
  },
  { additionalProperties: false },
);

export const SelfImprovementJobStatusSchema = Type.Union([
  Type.Literal("queued"),
  Type.Literal("running"),
  Type.Literal("shipped"),
  Type.Literal("failed"),
]);

export const SelfImprovementJobRecordSchema = Type.Object(
  {
    id: NonEmptyString,
    agentId: NonEmptyString,
    goal: NonEmptyString,
    youtrackProject: NonEmptyString,
    youtrackIssueId: Type.Optional(NonEmptyString),
    youtrackIssueReadable: Type.Optional(NonEmptyString),
    workerAgentId: NonEmptyString,
    requesterSessionKey: NonEmptyString,
    projectId: Type.Optional(NonEmptyString),
    workerSessionKey: Type.Optional(NonEmptyString),
    runId: Type.Optional(NonEmptyString),
    repoWorkspaceDir: Type.Optional(NonEmptyString),
    repoHeadAtStart: Type.Optional(NonEmptyString),
    changedPaths: Type.Optional(Type.Array(NonEmptyString)),
    status: SelfImprovementJobStatusSchema,
    error: Type.Optional(Type.String()),
    createdAtMs: Type.Integer({ minimum: 0 }),
    updatedAtMs: Type.Integer({ minimum: 0 }),
  },
  { additionalProperties: false },
);

export const FleetSelfImprovementStartParamsSchema = Type.Object(
  {
    agentId: NonEmptyString,
    sessionKey: NonEmptyString,
    goal: NonEmptyString,
    youtrackProject: Type.Optional(NonEmptyString),
    workerAgentId: Type.Optional(NonEmptyString),
    projectId: Type.Optional(NonEmptyString),
  },
  { additionalProperties: false },
);

export const FleetSelfImprovementStartResultSchema = Type.Object(
  {
    job: SelfImprovementJobRecordSchema,
    childSessionKey: NonEmptyString,
    runId: NonEmptyString,
    warning: Type.Optional(Type.String()),
    youtrackConfigured: Type.Boolean(),
  },
  { additionalProperties: false },
);

export const FleetSelfImprovementListParamsSchema = Type.Object(
  {
    agentId: NonEmptyString,
  },
  { additionalProperties: false },
);

export const FleetSelfImprovementListResultSchema = Type.Object(
  {
    agentId: NonEmptyString,
    jobs: Type.Array(SelfImprovementJobRecordSchema),
    youtrackConfigured: Type.Boolean(),
  },
  { additionalProperties: false },
);

export const FleetSelfImprovementStatusParamsSchema = Type.Object(
  {
    agentId: NonEmptyString,
    jobId: NonEmptyString,
  },
  { additionalProperties: false },
);

export const FleetSelfImprovementStatusResultSchema = Type.Object(
  {
    job: SelfImprovementJobRecordSchema,
    youtrackConfigured: Type.Boolean(),
  },
  { additionalProperties: false },
);

export const FleetSelfImprovementResolveWorkspaceParamsSchema = Type.Object(
  {
    agentId: NonEmptyString,
    sessionKey: Type.Optional(NonEmptyString),
    workerAgentId: Type.Optional(NonEmptyString),
  },
  { additionalProperties: false },
);

export const FleetSelfImprovementResolveWorkspaceResultSchema = Type.Object(
  {
    agentId: NonEmptyString,
    workerWorkspaceDir: NonEmptyString,
    sessionToolWorkspaceDir: Type.Optional(NonEmptyString),
    defaultAgentWorkspaceDir: NonEmptyString,
    youtrackConfigured: Type.Boolean(),
  },
  { additionalProperties: false },
);

// Multiagent template schemas
export const MultiagentTemplateAgentSchema = Type.Object(
  {
    id: NonEmptyString,
    name: NonEmptyString,
    role: NonEmptyString,
    capabilities: Type.Optional(Type.Array(Type.String())),
    tools: Type.Optional(Type.Array(Type.String())),
    order: Type.Optional(Type.Integer({ minimum: 0 })),
    description: Type.Optional(Type.String()),
  },
  { additionalProperties: false },
);

export const MultiagentTemplateWorkflowSchema = Type.Object(
  {
    type: Type.Union([
      Type.Literal("sequential"),
      Type.Literal("supervisor"),
      Type.Literal("parallel"),
      Type.Literal("custom"),
    ]),
    description: Type.Optional(Type.String()),
    flow: Type.Optional(Type.Array(NonEmptyString)),
    lead: Type.Optional(NonEmptyString),
    workers: Type.Optional(Type.Array(NonEmptyString)),
    final: Type.Optional(NonEmptyString),
    parallel: Type.Optional(Type.Array(NonEmptyString)),
    escalationPath: Type.Optional(NonEmptyString),
  },
  { additionalProperties: false },
);

export const MultiagentTemplateLimitsSchema = Type.Object(
  {
    maxIterations: Type.Optional(Type.Integer({ minimum: 1 })),
    maxTokens: Type.Optional(Type.Integer({ minimum: 1 })),
    maxCost: Type.Optional(Type.Number({ minimum: 0 })),
    maxDurationSeconds: Type.Optional(Type.Integer({ minimum: 1 })),
    maxConcurrentSubagents: Type.Optional(Type.Integer({ minimum: 1 })),
  },
  { additionalProperties: false },
);

export const MultiagentTemplateSchema = Type.Object(
  {
    id: NonEmptyString,
    name: NonEmptyString,
    description: Type.Optional(Type.String()),
    category: Type.Union([
      Type.Literal("development"),
      Type.Literal("security"),
      Type.Literal("research"),
      Type.Literal("data"),
      Type.Literal("support"),
      Type.Literal("custom"),
    ]),
    architecture: Type.Union([
      Type.Literal("sequential"),
      Type.Literal("supervisor"),
      Type.Literal("parallel"),
      Type.Literal("custom"),
    ]),
    agents: Type.Array(MultiagentTemplateAgentSchema),
    workflow: Type.Optional(MultiagentTemplateWorkflowSchema),
    limits: Type.Optional(MultiagentTemplateLimitsSchema),
    tags: Type.Optional(Type.Array(Type.String())),
  },
  { additionalProperties: false },
);

export const MultiagentTemplatesListParamsSchema = Type.Object(
  {
    query: Type.Optional(Type.String()),
    category: Type.Optional(Type.String()),
  },
  { additionalProperties: false },
);

export const MultiagentTemplatesListResultSchema = Type.Object(
  {
    templates: Type.Array(
      Type.Object(
        {
          id: NonEmptyString,
          name: NonEmptyString,
          description: Type.Optional(Type.String()),
          category: Type.String(),
          architecture: Type.String(),
          agentCount: Type.Integer({ minimum: 0 }),
        },
        { additionalProperties: false },
      ),
    ),
  },
  { additionalProperties: false },
);

export const MultiagentTemplatesGetParamsSchema = Type.Object(
  {
    id: NonEmptyString,
  },
  { additionalProperties: false },
);

export const MultiagentTemplatesGetResultSchema = MultiagentTemplateSchema;
